import React from 'react';

class UserFriendlyErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // You can log the error to an error reporting service here
    console.error('Application Error:', error, errorInfo);
  }

  handleRetry = () => {
    // Reset error state to try rendering again
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
          <div className="max-w-md w-full">
            {/* Error Icon */}
            <div className="text-center mb-6">
              <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                <svg 
                  className="w-8 h-8 text-red-600" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" 
                  />
                </svg>
              </div>
              
              {/* User-Friendly Message */}
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                Oops! Something went wrong
              </h1>
              
              <p className="text-gray-600 mb-6">
                We're experiencing a technical issue. Don't worry - your data is safe. 
                Please try refreshing the page or contact support if the problem persists.
              </p>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              <button
                onClick={this.handleRetry}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors"
              >
                Try Again
              </button>
              
              <button
                onClick={() => window.location.reload()}
                className="w-full bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-3 px-4 rounded-lg transition-colors"
              >
                Refresh Page
              </button>
              
              <div className="text-center pt-4">
                <p className="text-sm text-gray-500">
                  Need help? Contact our support team at{' '}
                  <a 
                    href="mailto:support@axel.com" 
                    className="text-blue-600 hover:text-blue-800 underline"
                  >
                    support@axel.com
                  </a>
                </p>
              </div>
            </div>
            
            {/* Technical Details (Hidden by default, can be shown in dev mode) */}
            {process.env.NODE_ENV === 'development' && (
              <details className="mt-8 bg-gray-100 rounded-lg p-4">
                <summary className="cursor-pointer text-sm text-gray-600 font-medium">
                  Technical Details (Dev Mode)
                </summary>
                <pre className="mt-2 text-xs text-gray-800 overflow-auto">
                  {this.state.error?.toString()}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default UserFriendlyErrorBoundary;
